/**
 * Popups API
 *
 * API endpoints for popups management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type {
  DeletePopupArgs,
  GetPopupByCompanyArgs,
  GetPopupsByCompanyArgs,
  GetPopupsUsersCount,
  GetPopupsViewsCount,
  Popup,
  PopupRaw,
  SavePopup,
  SavePopupUpload
} from "./popups.types";

/**
 * Popups API endpoints
 * Provides operations for popups management
 */
export const popupsApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get popups by company ID
     * @param id - Company ID
     * @returns Array of popups
     */
    getPopupsByCompany: build.query<PopupRaw[], GetPopupsByCompanyArgs, Popup[]>({
      query: ({ id }) => `/popups/company/${id}`,
      onQueryStarted: async ({ id }, { queryFulfilled, dispatch }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando pop-ups...",
          success: "Pop-ups carregados com sucesso!",
          error: "Erro ao carregar os pop-ups."
        });

        // Aguarda o resultado principal antes de buscar contagens relacionadas
        const { data } = await queryFulfilled;

        data.forEach(async (popup) => {
          const popupId = popup.id;
          const args: GetPopupsUsersCount["args"] = {
            id: popupId
          };

          let patchResult: { undo: () => void } | undefined;
          const popupsUsersCountPromise = dispatch(popupsApi.endpoints.getPopupsUsersCount.initiate(args));
          const popupsViewsCountPromise = dispatch(popupsApi.endpoints.getPopupsViewsCount.initiate(args));

          try {
            const [{ data: popupsUsersCountData }, { data: popupsViewsCountData }] = await Promise.all([popupsUsersCountPromise, popupsViewsCountPromise] as const);

            patchResult = dispatch(
              popupsApi.util.updateQueryData("getPopupsByCompany", { id }, (draft) => {
                const usersCount = popupsUsersCountData?.total ?? 0;
                const viewsCount = popupsViewsCountData?.total ?? 0;

                draft.forEach((popup) => {
                  popup.users_count = usersCount;
                  popup.views_count = viewsCount;
                });
              })
            );
          } catch (_error) {
            patchResult?.undo();
          } finally {
            popupsUsersCountPromise.unsubscribe();
            popupsViewsCountPromise.unsubscribe();
          }
        });
      },
      transformResponse: (response) => {
        const transformedResponse: PopupRaw[] = response.map((item) => ({
          ...item,
          users_count: 0,
          views_count: 0
        }));

        return transformedResponse;
      }
    }),

    /**
     * Get popup views count by popup ID
     * @param id - Popup ID
     * @returns Views count (typed via `GetPopupsViewsCount["response"]`)
     */
    getPopupsViewsCount: build.query<GetPopupsViewsCount["response"], GetPopupsViewsCount["args"]>({
      query: ({ id }) => `/popups/view-count/${id}`,
      extraOptions: {
        maxRetries: 0
      }
    }),

    /**
     * Get users count impacted by popup ID
     * @param id - Popup ID
     * @returns Users count (typed via `GetPopupsUsersCount["response"]`)
     */
    getPopupsUsersCount: build.query<GetPopupsUsersCount["response"], GetPopupsUsersCount["args"]>({
      query: ({ id }) => `/popups/users-count/${id}`,
      extraOptions: {
        maxRetries: 0
      }
    }),

    /**
     * Get popup by ID
     * @param id - Popup ID
     * @returns Popup details
     */
    getPopupByCompany: build.query<Popup, GetPopupByCompanyArgs>({
      query: ({ id }) => `/popups/${id}`
    }),

    /**
     * Save popup by company (create or update)
     * @param payload - Popup data to save
     * @returns void
     */
    savePopup: build.mutation<SavePopup["result"], SavePopup["args"]>({
      query: ({ $image: _, ...body }) => ({
        url: `/popups/save`,
        method: "POST",
        body
      }),
      onQueryStarted: async ({ $image }, { queryFulfilled, dispatch }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando pop-up...",
          success: "Pop-up salvo com sucesso!",
          error: "Erro ao salvar o pop-up."
        });

        const { data } = await queryFulfilled;

        if ($image) {
          dispatch(popupsApi.endpoints.uploadPopupImage.initiate({ id: data.id, image: $image }));
        }
      }
    }),

    /**
     * Delete popup
     * @param id - Popup ID
     * @returns void
     */
    deletePopup: build.mutation<unknown, DeletePopupArgs>({
      query: ({ id }) => ({
        url: `/popups/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (_args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Deletando pop-up...",
          success: "Pop-up deletado com sucesso!",
          error: "Erro ao deletar o pop-up."
        });
      }
    }),

    /**
     * Upload popup image
     * @param id - Popup ID
     * @param image - Image file to upload
     * @returns void
     */
    uploadPopupImage: build.mutation<SavePopupUpload["result"], SavePopupUpload["args"]>({
      query: ({ id, image }) => {
        const formData = new FormData();
        formData.append("id", String(id));
        formData.append("image", image);

        return {
          url: `/popups/upload`,
          method: "POST",
          headers: {
            "Content-Type": "multipart/form-data"
          },
          body: formData
        };
      }
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
popupsApi.enhanceEndpoints({
  addTagTypes: ["Popup", "PopupUsersCount", "PopupViewsCount"],
  endpoints: {
    getPopupsByCompany: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Popup" as const, id })), { type: "Popup", id: "LIST" }] : [{ type: "Popup", id: "LIST" }])
    },
    getPopupsUsersCount: {
      providesTags: (result, err, { id }) => [{ type: "PopupUsersCount", id }]
    },
    getPopupsViewsCount: {
      providesTags: (result, err, { id }) => [{ type: "PopupViewsCount", id }]
    },
    getPopupByCompany: {
      providesTags: (result, error, { id }) => [{ type: "Popup", id }]
    },
    savePopup: {
      invalidatesTags: [{ type: "Popup", id: "LIST" }]
    },
    deletePopup: {
      invalidatesTags: ["Popup"]
    },
    uploadPopupImage: {
      invalidatesTags: [{ type: "Popup", id: "LIST" }]
    }
  }
});
